import { logger } from '@lusha/client-logger';
import { flow, getRoot } from 'mobx-state-tree';
import { createUserOnDemandSettings, fetchOnDemandContacts, fetchUserOnDemandSettings, updateUserOnDemandInfo, updateUserOnDemandSettings, } from '../../../API/onDemand';
import { notifyFail } from '../../../hooks/useNotification';
import Contact from '../../Contact';
const Actions = self => ({
    fetchOnDemandSettings: flow(function* fetchOnDemandSettings() {
        var _a;
        try {
            const response = yield fetchUserOnDemandSettings();
            const emailSubscriptionEnabled = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.emailSubscription;
            self.setEmailSubscription(emailSubscriptionEnabled);
        }
        catch (err) {
            logger.warn('Failed to fetch on demand settings', { err });
        }
        self.setIsLoaded(true);
    }),
    setEmailSubscription(value) {
        self.emailSubscription = value;
    },
    createOnDemandSettings: flow(function* createOnDemandSettings() {
        yield createUserOnDemandSettings({ emailSubscription: true });
    }),
    updateOnDemandSettings: flow(function* updateOnDemandSettings(value) {
        yield updateUserOnDemandSettings({ emailSubscription: value });
    }),
    upsertOnDemandSettings: flow(function* upsertOnDemandSettings() {
        try {
            if (self.emailSubscription === null) {
                yield self.createOnDemandSettings();
            }
            else {
                yield self.updateOnDemandSettings(!self.emailSubscription);
            }
            self.setEmailSubscription(!self.emailSubscription);
        }
        catch (err) {
            logger.warn('Failed to update on demand settings', { err });
            notifyFail('Something went wrong, please try again later');
        }
    }),
    setIsLoaded(value) {
        self.isLoaded = value;
    },
    fetchUserOnDemandContacts: flow(function* fetchUserOnDemandContacts() {
        const { user } = getRoot(self);
        try {
            self.setIsFetchingContacts(true);
            self.onDemandContacts.clear();
            const res = yield fetchOnDemandContacts({ numberOfDaysFilter: self.numberOfDaysFilter });
            const { companies, metadata, contacts } = res || {};
            self.setOnDemandCompanies(companies);
            self.setRequestId(metadata === null || metadata === void 0 ? void 0 : metadata.requestId);
            self.setOnDemandContacts(contacts, metadata === null || metadata === void 0 ? void 0 : metadata.maskId);
            self.setIsFetchContactsError(false);
        }
        catch (e) {
            logger.error('Failed to fetch user on demand contacts', {
                err: e,
                accountId: user === null || user === void 0 ? void 0 : user.accountId,
                userId: user === null || user === void 0 ? void 0 : user.id,
            });
            self.setIsFetchContactsError(true);
        }
        finally {
            self.setIsFetchingContacts(false);
            self.setIsContactsFetched(true);
            self.setShouldReFetchContactsAfterShow(false);
        }
    }),
    setIsFetchingContacts(value) {
        self.isFetchingContacts = value;
    },
    addContact: (contact, companyId, maskedId) => {
        var _a;
        if (!getRoot(self).companies.get('emptySavedTabCompany')) {
            getRoot(self).setEmptyCompany('emptySavedTabCompany');
        }
        const key = (_a = contact.id) === null || _a === void 0 ? void 0 : _a.toString();
        const companyKey = (companyId === null || companyId === void 0 ? void 0 : companyId.toString()) || 'emptySavedTabCompany';
        const payload = {
            id: key,
            company: companyKey,
        };
        const employee = Contact.create(payload);
        employee.setSearchV2Contact(Object.assign(Object.assign({}, contact), { requestId: self.requestId, isFromDetailsFoundTab: true, maskedId, storeCompanyKey: companyKey, isOnDemandContactFound: true }));
        self.onDemandContacts.set(key, employee);
    },
    setOnDemandContacts(contacts = [], maskedId) {
        self.onDemandContacts.clear();
        const validContacts = contacts.filter(contact => {
            var _a, _b, _c;
            return contact.id &&
                (((_a = contact.emails) === null || _a === void 0 ? void 0 : _a.length) > 0 ||
                    (((_b = contact.phones) === null || _b === void 0 ? void 0 : _b.length) > 0 && ((_c = contact.phones) === null || _c === void 0 ? void 0 : _c.every(phone => !phone.do_not_call))));
        });
        self.numberOfOnDemandContacts = validContacts.length;
        validContacts.forEach(contact => {
            var _a;
            const companyId = ((_a = contact.company_lid) === null || _a === void 0 ? void 0 : _a.toString()) || contact.companyLid || contact.fqdn;
            self.addContact(contact, companyId, maskedId);
        });
    },
    setOnDemandCompanies(companies = []) {
        const root = getRoot(self);
        companies.forEach(company => root.addCompany(company));
    },
    setIsContactsFetched(value) {
        self.isContactsFetched = value;
    },
    setNumberOfDaysFilter(value) {
        self.numberOfDaysFilter = value;
    },
    setSelectedContactId(value) {
        self.selectedContactId = value;
    },
    setRequestId(value) {
        self.requestId = value;
    },
    setIsFetchContactsError(value) {
        self.isFetchContactsError = value;
    },
    setNumberOfNewOnDemandContacts(value) {
        self.numberOfNewOnDemandContacts = value;
    },
    initOnDemandInfo: flow(function* initOnDemandInfo() {
        try {
            const res = yield updateUserOnDemandInfo();
            const { onDemandContactsIndicator, onDemandSettings } = res || {};
            self.emailSubscription = onDemandSettings === null || onDemandSettings === void 0 ? void 0 : onDemandSettings.emailSubscription;
            self.setNumberOfNewOnDemandContacts((onDemandContactsIndicator === null || onDemandContactsIndicator === void 0 ? void 0 : onDemandContactsIndicator.numberOfNewContacts) || 0);
        }
        catch (e) {
            logger.error('Failed to fetch on demand info', { err: e });
        }
        self.setIsLoaded(true);
    }),
    setShouldReFetchContactsAfterShow(value) {
        self.shouldReFetchContactsAfterShow = value;
    },
    setIsOnDemandContactShown(value) {
        self.isOnDemandContactShown = value;
    },
});
export default Actions;
